import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";

// React Query
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Redux
import { useSelector } from "react-redux";

// Third party
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

// Custom hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";

// Helpers
import { dateValidation, emptyValidation } from "@/Layouts/components/Tables/DataSmartTable/validation/TableValidations";

// Services
import actionsList from "@/Constants/actionsList";

// Components
import { CustomPopper } from "@/Layouts/components/CustomPopper";
import RowFeedback from "@/Common/helpers/RowFeedback";
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import DataTableDateTimePicker from "@/Layouts/components/DateTimePicker/DataTableDateTimePicker";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import CustomCell from "@/Layouts/components/Tables/DataSmartTable/additional/CustomCell";
import TableInputValue from "@/Layouts/components/InputComponents/TableInputValue";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import HelpTooltip from "@/Layouts/components/Tables/DataSmartTable/additional/HelpTooltip";

// MUI
import { Autocomplete, Checkbox, CircularProgress, FormControl, Paper, Popover, TextField, Tooltip, Typography } from "@mui/material";
import { GridActionsCellItem, GridRowModes, useGridApiContext, useGridApiRef } from "@mui/x-data-grid-premium";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// Icons
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import CancelIcon from "@mui/icons-material/Close";
import { SaveIconComponent } from "@/Layouts/components/Tables/DataSmartTable/additional/SaveIconComponent";

// Constants
import {
	COLUMN_RESIZE_FACTOR,
	ONE_HUNDRED,
	ONE_HUNDRED_EIGHTY,
	ONE_HUNDRED_FIFTY,
	ONE_HUNDRED_THIRTY_FIVE,
	ONE_HUNDRED_TWENTY,
	TAILWIND_FOR_DATA_SMART_TABLE,
} from "@/Constants/tables";
import { DATE_FORMAT } from "@/Constants/date";
import { HELP_TOOLTIP_TITLE } from "@/Motivators/constants";

import { getAdministrationToolsOpportunityMining, getOpportunityMiningProviderInfo } from "@/Motivators/api/autoAlerts";
import { createIntervalObjects } from "@/Common/helpers/functions";
import { useSelectedValue } from "@/Motivators/context/AutoAlerts/SelectedValueContext";
import { accountFormat } from "@/Common/helpers/number";
import { SnackbarsActions, SnackbarsContent } from "@Layouts/components/Snackbars";
import { useSnackbar } from "notistack";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { CheckBoxOutlineBlank } from "@mui/icons-material";
import MultiSelectorLink from "./MultiSelectorLink";
import { compact } from "lodash-es";
import { useSectionMotivators } from "@Motivators/context/Common/MotivatorsSectionContext";
import { useMotivators } from "@Motivators/context/Common/MotivatorsContext";
import { maxHeight } from "@/Common/helpers/visuals";

const PaperComponent = ({ children }) => <Paper style={{ maxHeight: "200px", overflow: "auto" }}>{children}</Paper>;

const DescriptionPopover = ({ descriptions, counterText, charactersNumber }) => {
	const [anchorEl, setAnchorEl] = useState(null);
	const [hasTooltip, setHasTooltip] = useState(false);
	const descriptionRef = useRef(null);
	const [displayText, setDisplayText] = useState("");

	useEffect(() => {
		if (descriptions?.[0]) {
			if (descriptions[0].length > charactersNumber) {
				setDisplayText(`${descriptions[0].substring(0, charactersNumber)} ...`);
				setHasTooltip(true);
			} else {
				setDisplayText(descriptions[0]);
			}
		}
	}, []);

	const handleClick = () => {
		setAnchorEl(descriptionRef.current);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const open = Boolean(anchorEl);
	const id = open ? "simple-popover" : undefined;

	const displayTextContext = () => {
		const content = (
			<Typography aria-describedby={id} className={"regular-pt-sans-pro-narrow-label fs-15"} component={"span"} ref={descriptionRef}>
				{displayText}
			</Typography>
		);

		if (hasTooltip) {
			return (
				<Tooltip arrow title={descriptions[0]} classes={{ tooltip: "!font-regular-sans" }}>
					{content}
				</Tooltip>
			);
		}

		return content;
	};

	return (
		<div>
			{displayTextContext()}
			<Typography onClick={handleClick} className={"regular-pt-sans-pro-narrow-label fs-15 link-look !ml-[3px]"} component={"span"}>
				{counterText}
			</Typography>
			<Popover
				id={id}
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
			>
				<Typography sx={{ padding: "10px" }} className={"regular-pt-sans-pro-narrow-label fs-15"}>
					{descriptions.map((description) => (
						<Fragment key={uuidv4()}>
							<span>{description}</span>
							<br />
						</Fragment>
					))}
				</Typography>
			</Popover>
		</div>
	);
};

function InputEditDateTimeCell(props) {
	const apiRef = useGridApiContext();
	const { id, field, error, fromDate, toDate } = props;
	const [dateValue, setDateValue] = useState(props.value);

	const onChangeHandle = (value) => {
		const inputValue = moment(value).utc(false).format();
		// `debounceMs` is important to avoid the input to be updated too often.
		apiRef.current.setEditCellValue({ id, field, value: new Date(inputValue), debounceMs: 200 });
		setDateValue(new Date(inputValue));
	};

	return (
		<DataTableDateTimePicker
			hideTabs
			value={dateValue}
			minDateTime={moment(fromDate).utc(false).format(DATE_FORMAT.isoDate)}
			maxDateTime={moment(toDate)}
			defaultCalendarMonth={moment(fromDate)}
			disableIgnoringDatePartForTimeValidation
			disableHighlightToday
			format={DATE_FORMAT.monthAbbrDatePadded}
			views={["year", "month", "day"]}
			viewRenderers={{
				hours: null,
				minutes: null,
				seconds: null,
			}}
			error={error}
			onChange={onChangeHandle}
		/>
	);
}

const formatNames = (values, charactersNumber = 17) => {
	const names = values;
	let nameString;

	if (names && names?.length > 1) {
		nameString = `${names[0].length > charactersNumber ? names[0].substring(0, charactersNumber) + "..." : names[0]}, ${
			names?.length - 1
		} more`;
	} else {
		nameString = names[0].length > charactersNumber ? names[0].substring(0, charactersNumber) + "..." : names[0];
	}

	return nameString;
};

function InputEditActivityTypeCell(props) {
	const apiRef = useGridApiContext();
	const { id, field } = props;
	const [fieldStateError, setFieldStateError] = useState(true);
	const location = useSelector((state) => state.location.location);

	const {
		activityStatusError,
		selectedOptionNames,
		selectedValue,
		setSelectedValue,
		setSelectedOptionNames,
		allActivities,
		setAllActivities,
		setActionStatusError,
	} = useSelectedValue();

	const {
		data: valuesData,
		isLoading,
		isFetching,
	} = useQuery({
		queryKey: ["activitiesTypeAutoAlerts", location?.locationId],
		queryFn: async () => {
			const activitiesTypes = await getOpportunityMiningProviderInfo(location?.locationId, "");
			return activitiesTypes?.qualifyingActivityActionPairs?.activityType;
		},
		staleTime: Infinity,
		enabled: !!location?.locationId,
		placeholderData: [],
	});

	useEffect(() => {
		if (valuesData?.length && !allActivities?.length) {
			setAllActivities(valuesData);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [valuesData]);

	const linkNames = useMemo(() => {
		const isEditing = (id && apiRef.current.getCellMode(id, field)) || null;
		const cellValue = (id && apiRef.current.getCellValue(id, field)) || null;

		if (selectedValue && isEditing && cellValue?.length) {
			const names = selectedValue?.map((item) => item.description) || null;
			const hasNames = names && names.length > 0;
			if (hasNames) {
				apiRef.current.setEditCellValue({ id, field, value: selectedValue, debounceMs: 200 });
				return formatNames(names, 15);
			}
		}

		apiRef.current.setEditCellValue({ id, field, value: selectedValue, debounceMs: 200 });
		const names = selectedOptionNames?.activity;
		const hasNames = names && names.length > 0;

		if (hasNames) {
			return formatNames(names, 15);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedOptionNames, selectedValue]);

	useEffect(() => {
		const isEditing = (id && apiRef.current.getCellMode(id, field)) || null;
		const cellValue = (id && apiRef.current.getCellValue(id, field)) || null;
		if (isEditing && cellValue?.length && allActivities?.length) {
			const filteredOptions = cellValue.reduce((acc, cell) => {
				const matches = allActivities.filter((item) => item.description === cell.description);
				return acc.concat(matches);
			}, []);
			setSelectedValue(filteredOptions);
			setSelectedOptionNames((prevOptionNames) => {
				return { ...prevOptionNames, [id]: filteredOptions };
			});
			setFieldStateError(false);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allActivities, apiRef]);

	useEffect(() => {
		if (activityStatusError) {
			setFieldStateError(activityStatusError.statusValue);
			setActionStatusError({ statusValue: activityStatusError.statusValue });
		} else if (!activityStatusError) {
			setActionStatusError({ statusValue: true });
		}
	}, [activityStatusError, setActionStatusError]);

	return (
		<MultiSelectorLink
			id="activity"
			linkName={linkNames || "Select Activity Type"}
			loading={isLoading || isFetching}
			optionsData={valuesData}
			modalTitle="Activity Types"
			multiple={true}
			propsId={props.id}
			field={props.field}
			fieldStateError={fieldStateError || false}
			apiRef={apiRef}
		/>
	);
}

function InputEditActionTypeCell(props) {
	const apiRef = useGridApiContext();
	const { id, field } = props;
	const [fieldStateErrorAT, setFieldStateErrorAT] = useState(true);

	const {
		actionStatusError,
		selectedOptionNames,
		selectedValue,
		setCheckedActions,
		checkedActions,
		initiatedBySelection,
		allActionTypes,
		setAllActionTypes,
		setSelectedOptionNames,
		setActionStatusError,
	} = useSelectedValue();

	const linkNames = useMemo(() => {
		if (checkedActions) {
			const isEditing = (id && apiRef.current.getCellMode(id, field)) || null;
			const cellValue = (id && apiRef.current.getCellValue(id, field)) || null;

			if (isEditing && cellValue?.length && checkedActions?.length) {
				const names = checkedActions?.map((item) => item.description) || null;
				const hasNames = names && names.length > 0;
				if (hasNames) {
					apiRef.current.setEditCellValue({ id, field, value: checkedActions, debounceMs: 200 });
					return formatNames(names);
				}
			} else if (isEditing && !checkedActions?.length) {
				apiRef.current.setEditCellValue({ id, field, value: "", debounceMs: 200 });
				return null;
			} else if (isEditing && !cellValue?.length && checkedActions?.length) {
				const names = checkedActions?.map((item) => item.description) || null;
				apiRef.current.setEditCellValue({ id, field, value: checkedActions, debounceMs: 200 });
				return formatNames(names);
			}
		}

		const names = (selectedOptionNames && selectedOptionNames?.action) || null;
		const hasNames = names && names.length > 0;

		if (hasNames) {
			return formatNames(names);
		}
	}, [apiRef, checkedActions, field, id, selectedOptionNames]);

	const filteredLists = useMemo(() => {
		if (initiatedBySelection?.length) {
			const filterLists = initiatedBySelection.map((option) =>
				allActionTypes.filter((item) => item.customerInitiated === option._id)
			);

			return filterLists.flat();
		}
		return [];

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initiatedBySelection]);

	useEffect(() => {
		setAllActionTypes(allActionTypes);
		setCheckedActions(filteredLists);
		const names = filteredLists?.length ? filteredLists.map((item) => item.description) : null;
		setSelectedOptionNames((prevOptionNames) => {
			return { ...prevOptionNames, [props.id]: names };
		});

		setFieldStateErrorAT(names === null);

		if (!filteredLists?.length && actionStatusError && !actionStatusError?.statusValue) {
			setActionStatusError({ statusValue: false });
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filteredLists]);

	useEffect(() => {
		const isEditing = (id && apiRef.current.getCellMode(id, field)) || null;
		const cellValue = (id && apiRef.current.getCellValue(id, field)) || null;

		if (isEditing && cellValue?.length && selectedValue?.length) {
			let allActionTypes = selectedValue.map((option) => option?.actionType.filter((item) => item));
			allActionTypes = allActionTypes.flat();

			const filteredOptions = cellValue.reduce((acc, cell) => {
				const matches = allActionTypes.filter(
					(item) => item.description === cell.description && item.customerInitiated === cell.customerInitiated
				);

				// If there are multiple matches, choose only the first one
				return matches.length > 0 ? acc.concat(matches[0]) : acc;
			}, []);

			setAllActionTypes(allActionTypes);
			setCheckedActions(filteredOptions);
			setSelectedOptionNames((prevOptionNames) => {
				return { ...prevOptionNames, [props.id]: filteredOptions };
			});
			setFieldStateErrorAT(!filteredOptions?.length);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, selectedValue]);

	useEffect(() => {
		if (actionStatusError) {
			setFieldStateErrorAT(actionStatusError.statusValue);
		} else if (!actionStatusError) {
			setFieldStateErrorAT({ statusValue: true });
		}
	}, [actionStatusError]);

	return (
		<MultiSelectorLink
			id="action"
			linkName={linkNames || "Select Action Types"}
			contextData={selectedValue}
			messageError="Please select Activity Type first."
			modalTitle="Action Types"
			multiple={true}
			propsId={props.id}
			field={props.field}
			fieldStateError={fieldStateErrorAT || false}
		/>
	);
}

function InputEditInitiatedByCell(props) {
	const apiRef = useGridApiContext();
	const { id, field } = props;
	const [open, setOpen] = useState(false);
	const [options, setOptions] = useState([]);
	const [value, setValue] = useState([]);
	const [firstTimeOpenValues, setFirstTimeOpenValues] = useState(null);
	const [isSelectedAll, setIsSelectedAll] = useState(false);
	const { setInitiatedBySelection, checkedActions, allActionTypes } = useSelectedValue();

	const handleChange = (_event, values) => {
		setIsSelectedAll(false);
		if (!values?.length) {
			apiRef.current.setEditCellValue({ id, field, value: "", debounceMs: 200 });
			setValue([]);
		} else {
			const labels = values?.map((item) => item.label);
			apiRef.current.setEditCellValue({ id, field, value: labels, debounceMs: 200 });
			setValue(values);
		}
	};

	function getInitiatedByOptions(data) {
		if (!Array.isArray(data)) {
			return [];
		}
		const customerInitiatedArray = [];
		for (const item of data) {
			customerInitiatedArray.push(item.customerInitiated);
		}

		const dealershipObj = { _id: "0", label: "Dealership" };
		const customerObj = { _id: "1", label: "Customer" };

		if (!customerInitiatedArray.includes("1")) {
			return [dealershipObj];
		} else if (!customerInitiatedArray.includes("0")) {
			return [customerObj];
		} else {
			return [customerObj, dealershipObj];
		}
	}

	const handleCheckBoxClick = (event, selected, value, option) => {
		event.stopPropagation();
		setFirstTimeOpenValues(true);

		let newValue;
		if (selected) {
			newValue = value.filter((v) => v.label !== option.label);
		} else {
			newValue = value && Array.isArray(value) && value.length ? [...value, option] : [option];
		}

		handleChange(event, newValue);
	};

	useEffect(() => {
		const isEditing = apiRef.current.getCellMode(id, field);
		const cellValue = apiRef.current.getCellValue(id, field);
		let valuesOpt = [];
		let customerInitiatedCount = 0;
		let dealershipInitiatedCount = 0;
		let allActionsAreChecked = false;
		if (checkedActions?.length >= 1 && allActionTypes?.length >= 1) {
			allActionsAreChecked = checkedActions.length === allActionTypes.length;
		}
		const countInitiatedByCustomer = checkedActions?.filter((item) => item.customerInitiated === "1")?.length;
		const countInitiatedByDealership = checkedActions?.filter((item) => item.customerInitiated === "0")?.length;
		allActionTypes?.forEach((item) => {
			if (item.customerInitiated === "1") customerInitiatedCount += 1;
			else if (item.customerInitiated === "0") dealershipInitiatedCount += 1;
		});

		if (allActionsAreChecked) {
			valuesOpt = getInitiatedByOptions(allActionTypes);
			setIsSelectedAll(true);
			setValue(valuesOpt);
			setOptions(valuesOpt);
			handleChange(null, valuesOpt);
		} else if (customerInitiatedCount === countInitiatedByCustomer && checkedActions.length === countInitiatedByCustomer) {
			valuesOpt = getInitiatedByOptions(allActionTypes);

			setOptions(valuesOpt);
			const filteredValues = valuesOpt.filter((item) => item.label !== "Dealership");
			setValue(filteredValues);
			handleChange(null, filteredValues);
		} else if (dealershipInitiatedCount === countInitiatedByDealership && checkedActions.length === countInitiatedByDealership) {
			valuesOpt = getInitiatedByOptions(allActionTypes);

			setOptions(valuesOpt);
			const filteredValues = valuesOpt.filter((item) => item.label !== "Customer");
			setValue(filteredValues);
			handleChange(null, filteredValues);
		} else if (isEditing && Array.isArray(cellValue) && !allActionTypes) {
			setOptions([
				{ _id: "Dealership", label: "Dealership" },
				{ _id: "Customer", label: "Customer" },
			]);
			setValue(cellValue);
			handleChange(null, cellValue);
		} else if (Array.isArray(cellValue) && cellValue?.length && !checkedActions?.length) {
			setOptions(getInitiatedByOptions(allActionTypes));
			setValue([]);
			handleChange(null, []);
		} else if (Array.isArray(cellValue) && cellValue?.length) {
			setOptions(getInitiatedByOptions(allActionTypes));
			if (checkedActions?.length) {
				setValue([]);
				handleChange(null, []);
			} else {
				setValue(cellValue);
				handleChange(null, cellValue);
			}
		} else {
			setOptions(getInitiatedByOptions(allActionTypes));
			setValue([]);
			handleChange(null, []);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [checkedActions, apiRef, id, field]);

	return (
		<FormControl required fullWidth>
			<Autocomplete
				multiple={true}
				disableCloseOnSelect
				value={value}
				className={clsx({
					"advanced-autocomplete-error": props?.error,
					"advanced-select-open": open,
					"advance-select-expand-area": true,
					[props?.className]: props.className,
				})}
				getOptionLabel={(option) => (option?.label ? option.label : "")}
				options={options || []}
				onChange={handleChange}
				isOptionEqualToValue={(option, value) => option.label === value.label}
				renderOption={(props, option, { selected }) => {
					return (
						<li
							{...props}
							style={{
								display: "flex",
								alignItems: "center",
							}}
						>
							<Checkbox
								icon={<CheckBoxOutlineBlank style={{ fontSize: "17px" }} />}
								checkedIcon={<CheckBoxIcon style={{ fontSize: "17px" }} />}
								style={{
									paddingLeft: "8px",
									paddingRight: "12px",
									height: "5px",
									width: "5px",
								}}
								sx={{
									"&:hover": {
										bgcolor: "transparent",
									},
								}}
								checked={isSelectedAll || selected}
								onClick={(event) => {
									handleCheckBoxClick(event, selected, value, option);
								}}
							/>
							<Typography
								title={option.label}
								className={"regular-pt-sans-pro-narrow-label fs-15 auto-alerts-InputEditInitiatedByCell"}
								component={"div"}
							>
								{option.label}
							</Typography>
						</li>
					);
				}}
				ListboxProps={{
					className: "advanced-select-dropdown",
					sx: { maxHeight: maxHeight() || "200px", maxWidth: "200px" },
				}}
				PopperComponent={CustomPopper}
				PaperComponent={PaperComponent}
				renderTags={(value, getTagProps) => {
					let onDelete = () => {};
					if (value.length) {
						const tagProps = getTagProps({ index: 0 });
						onDelete = tagProps.onDelete || onDelete;
						const props = { ...tagProps };
						delete props.onDelete;

						return [
							<Typography
								sx={{
									fontFamily: "PTSansProNarrow",
									fontSize: "14.5px",
									color: "var(--text-color)",
									width: "45px",
									minWidth: "45px",
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
									position: "absolute",
								}}
								key={value[0].label}
								{...props}
							>
								{value[0].label}
							</Typography>,
						];
					}
					return [];
				}}
				renderInput={(params) => {
					const { InputProps } = params;
					const { endAdornment } = InputProps;

					// Create a new endAdornment
					const newEndAdornment = (
						<>
							{endAdornment}
							{props.loading && <CircularProgress color="inherit" size={20} className="your-progress-class" />}
						</>
					);

					return (
						<TextField
							error={props.error || false}
							{...params}
							variant="outlined"
							placeholder={value && value?.length ? "" : "Action Type"}
							className={clsx({
								"advanced-autocomplete-error": props?.error,
								"advanced-select-open": true,
								"advance-select-expand-area": true,
							})}
							InputProps={{
								...InputProps,
								style: { maxWidth: "200px", maxHeight: "28px", fontSize: "5px" },
								endAdornment: newEndAdornment,
							}}
						/>
					);
				}}
				onOpen={() => {
					setOpen(true);
					setFirstTimeOpenValues(value?.length ? value : null);
				}}
				onClose={() => {
					setOpen(false);
					if (!firstTimeOpenValues && !value?.length) {
						return false;
					}
					setInitiatedBySelection(value || []);
					return true;
				}}
				popupIcon={<ArrowForwardIosRoundedIcon size="small" sx={{ padding: "4px", transform: "rotate(90deg)" }} />}
			/>
		</FormControl>
	);
}

function InputEditCommissionCell(props) {
	const apiRef = useGridApiContext();
	const { id, field, hasFocus } = props;
	const [value, setValue] = useState(props.value?.value || 0);
	const [inputType, setInputType] = useState(props.value?.input || "Dollar");

	const handleChangeType = (_value, input) => setInputType(input);

	const handleActionUpdate = (valueData, input) => {
		setValue(valueData);
		apiRef.current.setEditCellValue({ id, field, value: { value: valueData, input } });
	};

	return (
		<TableInputValue
			isControlledApi={true}
			isEditingMode={hasFocus}
			isRequiredOnChange={true}
			data={[
				{
					type: "Dollar",
					min: 0,
					max: 10000,
					decimalsEditable: true,
					decimals: 2,
				},
				{
					type: "Percent",
					min: 0,
					max: 100,
					decimalsEditable: true,
					decimals: 2,
				},
			]}
			value={{
				value,
				input: inputType,
			}}
			defaultType={inputType}
			sx={{ height: "27px" }}
			onChange={(updated) => setValue(updated)}
			onBlur={handleActionUpdate}
			error={!value}
			onChangeType={handleChangeType}
			classes={"[&_.MuiButtonBase-root]:!h-[27px]"}
		/>
	);
}

function InputEditAssocWindowCell(props) {
	const apiRef = useGridApiContext();
	const location = useSelector((state) => state.location.location);
	const { id, field, error } = props;
	const [open, setOpen] = useState(false);
	const [value, setValue] = useState(null);

	const {
		data: valuesData,
		isLoading,
		isFetching,
	} = useQuery({
		queryKey: ["assocWindowForAutoAlerts", location?.locationId],
		queryFn: async () => {
			// We need to get AssocWindow on AdministrationTool for this location
			let associationWindowNumber;

			const assocWindow = await getAdministrationToolsOpportunityMining(location?.locationId);
			const configuration = assocWindow?.integrations?.opportunityMining?.provider[0]?.configuration || [];
			if (configuration && configuration.length > 0) {
				const associationWindowObj = configuration.find((obj) => obj.associationWindow);
				if (associationWindowObj) {
					const associationWindowValue = associationWindowObj.associationWindow;
					associationWindowNumber = parseInt(associationWindowValue, 10);
				}
			}

			return createIntervalObjects(associationWindowNumber || 90) || [];
		},

		staleTime: Infinity,
		enabled: !!location?.locationId,
		placeholderData: [],
	});

	const handleChange = (values) => {
		if (values) {
			// `debounceMs` is important to avoid the input to be updated too often.
			apiRef.current.setEditCellValue({ id, field, value: values.description, debounceMs: 200 });
			setValue(values.description);
		}
	};

	useEffect(() => {
		if (!props.value) return;
		setValue(props.value);
		return () => {
			setValue(null);
		};
	}, [props.value]);

	const selected = valuesData?.find((item) => item.description === value) ?? null;

	return (
		<SmartSelector
			showBy={"description"}
			options={valuesData}
			selected={selected}
			inputProps={{
				placeholder: "Select Association",
				classes:
					"[&_.MuiInputBase-root]:!min-h-[26px] [&_.MuiInputBase-root]:!py-[3px] [&_.MuiInputBase-root_.MuiInputBase-input]:!rounded-none",
			}}
			listBoxProps={{
				sx: { maxHeight: "223px" },
			}}
			error={error && !value}
			loading={isLoading || isFetching}
			onChangeAction={handleChange}
		/>
	);
}

export default function AutoAlertsTable(props) {
	// Table API ref
	const apiRef = useGridApiRef();
	const { setTableApiRef, setTableRows, commissionCycle } = useSectionMotivators();

	const newlyAddedRow = useRef(null);

	// Global States
	const user = useSelector((state) => state.user.user);

	const queryClient = useQueryClient();

	// Hooks
	const { motivatorType, mutations, subscriptions, isLoading } = useMotivators();
	const { enqueueSnackbar } = useSnackbar();
	const { getSubscription } = useSubscriptions();

	// Auth
	const { useAuth } = useAuthorization();
	const editAutoAlerts = useAuth(actionsList.EDIT_OPPORTUNITY_MINING);
	// Queries / Mutations
	const { mutate: saveAutoAlert } = useMutation({
		mutationFn: (input) =>
			new Promise((resolve, reject) => {
				const autoAlertsSetupId = props.autoAlertsSetupId.includes("_")
					? props.autoAlertsSetupId.split("_")[1]
					: props.autoAlertsSetupId;
				mutations
					.createItem({
						autoAlertsSetupId,
						locationId: props?.locationId,
						commissionCycleId: props?.commissionCycleId,
						row: input,
					})
					.then((response) => resolve(response))
					.catch((error) => reject(error));
			}),
	});
	const { mutate: updateAutoAlert } = useMutation({
		mutationFn: (input) =>
			new Promise((resolve, reject) => {
				mutations
					.updateItem({ input, locationId: props?.locationId, commissionCycleId: props?.commissionCycleId, userName: user._id })
					.then((response) => resolve(response))
					.catch((error) => reject(error));
			}),
	});
	const { mutate: deleteRows } = useMutation({
		mutationFn: (rows) =>
			new Promise((resolve, reject) => {
				const autoAlertSetUpId = props.autoAlertsSetupId.includes("_")
					? props.autoAlertsSetupId.split("_")[1]
					: props.autoAlertsSetupId;
				mutations
					.deleteItem({
						commissionCycleId: props.commissionCycleId,
						itemIds: rows,
						locationId: props?.locationId,
						setupId: autoAlertSetUpId,
					})
					.then((response) => resolve(response))
					.catch((error) => reject(error));
			}),
	});

	//Reset fields Activity Type, Action Type, Initiated By
	const {
		setActionStatusError,
		setActivityStatusError,
		setSelectedValue,
		setSelectedOptionNames,
		setSelectedActionTypeValue,
		setInitiatedBySelection,
	} = useSelectedValue();

	// Rows should be passed to a state to be able to update them without side effect.
	// Mutate the cache to update the rows will work as well.
	const [rows, setRows] = useState([]);
	// Controls the edit modes using a state.
	const [rowModesModel, setRowModesModel] = useState({});

	// Controls the selected rows using a state.
	const [selectedRows, setSelectedRows] = useState([]);

	// Controls to know if some row is loading. So the add and delete buttons can be disabled.
	const [rowLoading, setRowLoading] = useState(false);

	// This it's the correct way to listen to the edit mode changes.
	const editModeActive = useMemo(() => Object.values(rowModesModel)?.some(({ mode }) => mode === GridRowModes.Edit), [rowModesModel]);

	const cleanUpEditionMode = (item, operation) => {
		if (item && operation === "delete") {
			setRowModesModel((prev) => ({
				...prev,
				[item._id]: { mode: GridRowModes.View },
			}));
		}

		newlyAddedRow.current = null;
	};

	const handleRowEditStart = (_params, event) => {
		if (editModeActive) {
			event.defaultPrevented = true;
			event.defaultMuiPrevented = true;
		}
	};

	const handleRowEditStop = (_params, event) => {
		event.defaultMuiPrevented = true;
	};

	const handleSubscriptionResponse = (responseDataSource) => (data) => {
		if (responseDataSource?.items) {
			responseDataSource.items = responseDataSource.items.map((item) => {
				item.initiatedBy = item.initiatedBy?.map((initiator) => ({ _id: initiator, label: initiator })).filter(Boolean);
				return item;
			});
		}

		if (!responseDataSource?.items) {
			return data;
		}

		return responseDataSource || data;
	};

	/********************************************* SUBSCRIPTIONS *********************************************/
	const id = props?.selectedItem?._id?.includes("_") ? props?.selectedItem?._id.split("_")[1] : props?.selectedItem?._id;
	const autoAlertsSubscriptions = getSubscription([`${motivatorType}Body`, props.locationId, id]);
	const setSubscriptionCallbacks = (subscriptionName) => {
		const cacheId = props.autoAlertsSetupId.includes("_") ? props.autoAlertsSetupId.split("_")[1] : props.autoAlertsSetupId;

		autoAlertsSubscriptions?.subscriptions?.[subscriptionName]?.setCallback((response) => {
			const allQueries = queryClient.getQueryCache().findAll();
			const cachesToUpdate = allQueries.filter((query) => query.queryHash.includes(cacheId));

			cachesToUpdate.forEach((cache) => {
				queryClient.setQueryData(cache.queryKey, (cachedData) =>
					handleSubscriptionResponse(response.data[subscriptions.get(subscriptionName)?.getter])(cachedData)
				);
			});

			if (subscriptionName === "onDeleteItem") {
				cleanUpEditionMode(response.data[subscriptions.get(subscriptionName)?.getter], "delete");
			} else {
				cleanUpEditionMode();
			}
		});
	};

	setSubscriptionCallbacks("onUpdateItem");
	setSubscriptionCallbacks("onCreateItem");
	setSubscriptionCallbacks("onDeleteItem");

	// Thus fn is called when the user clicks on the "Save" button.
	const handleSaveClick = (id) => {
		const rowEdit = apiRef.current.state.editRows;
		const row = Object.values(rowEdit)[0];
		const keys = Object.keys(row);
		const errors = keys.filter((key) => key !== "initiatedBy" && (row[key].error || !row[key].value));

		if (!errors?.length) {
			setRowModesModel((prev) => ({
				...prev,
				[id]: { mode: GridRowModes.View },
			}));
		} else if (errors?.length === keys.length) {
			apiRef.current.setState((state) => ({
				...state,
				editRows: {
					...state.editRows,
					[id]: {
						...state.editRows[id],
						...errors.reduce((acc, key) => ({ ...acc, [key]: { ...state.editRows[id][key], error: true } }), {}),
					},
				},
			}));
		}
	};

	// This fn is called when the user clicks on the "Cancel" button
	const handleCancelClick = (id) => {
		setRowModesModel({
			...rowModesModel,
			[id]: { mode: GridRowModes.View, ignoreModifications: true },
		});

		const editedRow = rows.find((row) => row._id === id);
		if (editedRow?.isNew) {
			setRows(rows.filter((row) => row._id !== id));
		}
	};

	// This fn is called when the user clicks on the "Delete" button.
	const handleDeleteClick = async (ids, callback) => {
		setRowLoading(true);

		ids?.forEach((id) => {
			RowFeedback.skeleton(id, apiRef).delete().display();
		});

		deleteRows(ids, {
			onSuccess: () => {
				setRows((prev) => prev.filter((row) => !ids.includes(row._id)));
				setSelectedRows((prev) => prev.filter((id) => !ids.includes(id)));
				if (callback) {
					callback(true);
				}
			},
			onError: (error) => {
				const message = error?.toString().replace(/Error:/gi, " ");
				ids.forEach((id) => {
					RowFeedback.skeleton(id, apiRef).errorDelete();

					enqueueSnackbar(<SnackbarsContent message={message} critical={true} />, {
						variant: "error",
						preventDuplicate: true,
						action: (key) => <SnackbarsActions key={key} index={key} />,
					});
					if (callback) {
						callback(false);
					}
				});
			},
			onSettled: () => {
				setRowLoading(false);
			},
		});
	};

	// This fn is called when the user clicks on the "Add" button.
	const handleAddRow = () => {
		resetInputsSelectable();
		const key = uuidv4();

		// Define a default row initial values.
		const newRow = {
			_id: key,
			isNew: true,
			alertDate: null,
			activityType: null,
			actionType: [],
			initiatedBy: "",
			associationWindow: "",
			commission: "",
			createdBy: user,
		};

		// Add the new row to the row state.
		setRows((prev) => [newRow, ...prev]);

		// Set the new row in edit mode.
		setRowModesModel((prev) => ({
			...prev,
			[key]: { mode: GridRowModes.Edit },
		}));
	};

	// This fn is called when the user clicks on the "Add" button.
	const handleRowSelected = (newSelection) => {
		setSelectedRows(newSelection);
	};

	// This fn is call after the row is added or edited, and it should be used to save data to the backend.
	const handleProcessRowUpdate = async (newRow, oldRow) => {
		// compare the old row with the new row to know if changes were made.
		const isModified = Object.keys(newRow).some((key) => newRow[key] !== oldRow?.[key]);
		if (!isModified) {
			return oldRow;
		}

		const updatedRow = { ...newRow, isNew: false, updatedBy: user._id };
		let actionTypeIds = [];
		let activityTypeIds = [];
		actionTypeIds = newRow.actionType?.map((item) => item?._id).filter(Boolean);
		activityTypeIds = newRow.activityType?.map((item) => item?._id).filter(Boolean);

		const input = {
			...newRow,
			actionType: actionTypeIds,
			activityType: activityTypeIds,
			initiatedBy: newRow.initiatedBy,
			createdBy: user._id,
			alertDate: new Date(newRow.alertDate),
		};

		const copyInput = { ...input };

		delete input.isNew;

		setRowLoading(true);

		// Here you should save the data to the backend.
		RowFeedback.skeleton(updatedRow._id, apiRef).display();

		// Mutation operations
		let result = null;
		const mutateOperation = (id, apiRef, resolve) => ({
			onSuccess: (response) => {
				RowFeedback.skeleton(id, apiRef).success();
				resolve?.(response);
			},
			onError: (err) => {
				RowFeedback.skeleton(id, apiRef).error();
				console.log(err);
			},
			onSettled: () => {
				setRowLoading(false);
			},
		});

		if (newRow?.isNew) {
			newlyAddedRow.current = copyInput;
			result = new Promise((resolve) => {
				saveAutoAlert(input, mutateOperation(newRow._id, apiRef, resolve));
			});
		} else {
			delete input.lastModifiedOn;
			newlyAddedRow.current = copyInput;
			result = new Promise((resolve) => {
				input["createAt"] = input.createdAt;
				delete input.createdAt;
				updateAutoAlert(input, mutateOperation(input._id, apiRef, resolve));
			});
		}

		if (result) {
			await result;
		}

		setRows((prev) => prev.map((row) => (row._id === updatedRow._id ? updatedRow : row)));
		return updatedRow;
	};

	// This fn is call if an error was thrown when sending data to the backend.
	const handleProcessRowUpdateError = (error) => {
		// Here you should handle the error.
		console.error(error);
	};

	// This fn is gonna reset the inputs selectable (Activity Type, Action Type, Initiated By)
	const resetInputsSelectable = () => {
		setActionStatusError(null);
		setActivityStatusError(null);
		setSelectedValue(null);
		setSelectedOptionNames(null);
		setSelectedActionTypeValue(null);
		setInitiatedBySelection(null);
	};

	const columns = [
		{ field: "_id", headerName: "ID", width: 50, filterable: false },
		{
			field: "alertDate",
			headerName: "Rule Eff. Date",
			type: "date",
			width: ONE_HUNDRED_THIRTY_FIVE,
			editable: editAutoAlerts,
			resizable: false,
			renderEditCell: (params) => {
				const isAlertDateEditable = params.row.isNew || !moment(params.value).utc().endOf("day").isBefore(moment());
				if (isAlertDateEditable || props?.masterPermission) {
					const fromDate = props?.masterPermission
						? moment.max(moment(props?.selectedItem?.effectiveDateFrom), moment(commissionCycle.startDate))
						: moment.max(moment(props.dateRange.from), moment().add(1, "day"));

					return <InputEditDateTimeCell {...params} value={params.value} fromDate={fromDate} toDate={props.dateRange.to} />;
				}
				return undefined;
			},
			preProcessEditCellProps: (params) => {
				const valueExists = emptyValidation(params.props?.value);
				const valueIsDate = dateValidation(params.props?.value);
				return { ...params?.props, error: !valueExists || !valueIsDate };
			},
			valueFormatter: (value) => {
				const dateVal = moment(value).utc().format(DATE_FORMAT.monthAbbrDatePadded);
				return value ? dateVal : undefined;
			},
			renderCell: (row) => <CustomCell value={row.formattedValue ?? row.value} />,
		},
		{
			field: "createdBy",
			headerName: "User",
			width: ONE_HUNDRED_TWENTY,
			minWidth: ONE_HUNDRED_TWENTY,
			maxWidth: ONE_HUNDRED_TWENTY * COLUMN_RESIZE_FACTOR,
			resizable: true,
			valueFormatter: (value) => {
				if (value?.fullName) {
					return value.fullName;
				}
				return value;
			},
			renderCell: (row) => <CustomCell value={row.formattedValue ?? row.value} hasTooltip maxWith={row.colDef.width} />,
		},
		{
			field: "activityType",
			headerName: "Activity Type",
			width: 150,
			editable: editAutoAlerts,
			resizable: false,
			renderEditCell: (params) => <InputEditActivityTypeCell {...params} />,
			preProcessEditCellProps: (params) => {
				const valueExists = emptyValidation(params.props.value);
				return { ...params.props, error: !valueExists };
			},
			renderCell: (param) => {
				if (param?.value && Array.isArray(param.value)) {
					const elementCounter = param?.value?.length > 1 ? `${param?.value?.length - 1} more` : "";
					const descriptions = param?.value.map((item) => item?.description) || [];
					return <DescriptionPopover descriptions={descriptions} counterText={elementCounter} charactersNumber={15} />;
				}
				return undefined;
			},
		},
		{
			field: "actionType",
			headerName: "Action Type",
			width: ONE_HUNDRED_EIGHTY,
			editable: editAutoAlerts,
			resizable: false,
			renderEditCell: (params) => <InputEditActionTypeCell {...params} />,
			preProcessEditCellProps: (params) => {
				const valueExists = emptyValidation(params.props.value);
				return { ...params.props, error: !valueExists };
			},
			renderCell: (param) => {
				if (param?.value && Array.isArray(param.value)) {
					const elementCounter = param?.value?.length > 1 ? `${param?.value?.length - 1} more` : "";
					const descriptions = param?.value.map((item) => item?.description) || [];
					return <DescriptionPopover descriptions={descriptions} counterText={elementCounter} charactersNumber={17} />;
				}
				return undefined;
			},
		},
		{
			field: "initiatedBy",
			headerName: "Initiated By",
			width: ONE_HUNDRED_TWENTY,
			minWidth: ONE_HUNDRED_TWENTY,
			maxWidth: ONE_HUNDRED_TWENTY * COLUMN_RESIZE_FACTOR,
			editable: editAutoAlerts,
			resizable: true,
			renderEditCell: (params) => <InputEditInitiatedByCell {...params} />,
			valueFormatter: (value) => {
				if (!value || !Array.isArray(value) || value.length === 0) {
					return undefined;
				}

				if (typeof value[0] === "string") {
					return value.join(", ");
				}

				if (typeof value[0] === "object") {
					return value
						.map((item) => {
							if (typeof item?.label === "object") {
								return item?.label?._id ?? "";
							}
							return item?.label;
						})
						.join(", ");
				}

				return undefined;
			},
			renderCell: (row) => <CustomCell value={row.formattedValue ?? row.value} hasTooltip maxWith={row.colDef.width} />,
		},
		{
			field: "associationWindow",
			headerName: "Assoc. Window",
			width: ONE_HUNDRED_FIFTY,
			editable: editAutoAlerts,
			resizable: false,
			renderEditCell: (params) => <InputEditAssocWindowCell {...params} />,
			preProcessEditCellProps: (params) => {
				const valueExists = emptyValidation(params.props.value, { minLength: 4, maxLength: 10 });
				return { ...params.props, error: !valueExists };
			},
			valueFormatter: (param) => {
				if (param.value) {
					return param.value?.description || param.value;
				}
				return param;
			},
			renderCell: (row) => <CustomCell value={row.value} />,
		},
		{
			field: "commission",
			headerName: "Comm.",
			width: ONE_HUNDRED,
			editable: editAutoAlerts,
			align: "right",
			resizable: false,
			renderEditCell: (params) => <InputEditCommissionCell {...params} />,
			preProcessEditCellProps: (params) => {
				const valueExists = emptyValidation(params.props.value);
				return { ...params.props, error: !valueExists };
			},
			cellClassName: (params) => {
				if (params?.cellMode === "view") {
					return clsx({
						"cell-with-dollar-symbol": params.value?.input === "Dollar",
						"cell-with-percent-symbol": params.value?.input === "Percent",
					});
				}
				return null;
			},
			valueFormatter: (value) => {
				if (typeof value === "object") {
					return accountFormat(value?.value || 0);
				}
				return accountFormat(value || 0);
			},
		},
		{
			field: "actions",
			type: "actions",
			width: 80,
			resizable: false,
			getActions: ({ id }) => {
				const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
				if (isInEditMode) {
					return [
						<GridActionsCellItem
							key="saveAuto"
							icon={<SaveIconComponent />}
							label="Save"
							onClick={() => handleSaveClick(id)}
						/>,
						<GridActionsCellItem
							key="cancel"
							icon={<CancelIcon />}
							label="Cancel"
							className="textPrimary"
							onClick={() => handleCancelClick(id)}
							color="inherit"
						/>,
					];
				}

				return [];
			},
		},
	];

	const isComponentLoading = isLoading || rowLoading;

	const getRows = (allRows = [], commissionCycleObj = "") => {
		if (!commissionCycleObj || !allRows?.length) {
			return [];
		}

		return allRows.filter((row) => {
			const rowEffectiveDate = moment(row.alertDate).utc().endOf("day");
			return rowEffectiveDate.isSameOrAfter(commissionCycleObj.startDate) || row.isNew;
		});
	};

	const rowsFiltered = getRows(rows, commissionCycle);

	useEffect(() => {
		const newRow = rows.find((row) => row.isNew);

		if (editModeActive) {
			setRows(compact([newRow, ...props.data]));
			setTableRows(compact([newRow, ...props.data]));
		} else {
			setTableRows(compact(props.data));
			setRows(compact(props.data));
		}

		setTableApiRef(apiRef);
		return () => {
			setRows([]);
		};
	}, [props.data]);

	return (
		<DataSmartTable
			columns={columns}
			rows={rowsFiltered}
			rowHeight={28}
			apiRef={apiRef}
			editMode="row"
			maxRows={14}
			enableCustomScrollbars
			disableColumnMenu
			disableRowSelectionOnClick
			autoHeight={false}
			checkboxSelection
			emptyTableImageHeight="452px"
			isRowSelectable={() => !editModeActive && Boolean(editAutoAlerts)}
			onRowSelectionModelChange={handleRowSelected}
			rowModesModel={rowModesModel}
			processRowUpdate={handleProcessRowUpdate}
			onProcessRowUpdateError={handleProcessRowUpdateError}
			onRowModesModelChange={(newModel) => setRowModesModel(newModel)}
			onRowEditStart={handleRowEditStart}
			onRowEditStop={handleRowEditStop}
			columnVisibilityModel={{
				_id: false,
			}}
			slots={{
				toolbar: CommonToolbar,
				noRowsOverlay: NoRowsOverlay,
			}}
			slotProps={{
				toolbar: {
					customComponent: <HelpTooltip title={HELP_TOOLTIP_TITLE} />,
					filter: {
						hide: !!selectedRows.length,
						disabled: editModeActive,
					},
					search: {
						columns,
						data: rows || [],
						hide: !!selectedRows.length,
						disabled: editModeActive,
						searchInNestedArrays: ["activityType", "actionType", "initiatedBy"],
					},
					add: {
						onAddAction: handleAddRow,
						hide: !!selectedRows.length,
						disabled: editModeActive || !editAutoAlerts || !props.canAddItems || isComponentLoading,
					},
					delete: {
						onDeleteAction: handleDeleteClick,
						deleteTitle: "Remove",
						hide: !selectedRows.length,
						disabled: !editAutoAlerts || !props.canAddItems || rowLoading || isComponentLoading,
					},
				},
				noRowsOverlay: {
					title: "No Auto Alerts Data",
				},
			}}
			hideFooter
			customProps={{
				tableClassName: {
					"functional-table": true,
					"edit-table": true,
					...TAILWIND_FOR_DATA_SMART_TABLE,
				},
				expandableRows: false,
				hide: !rows?.length,
				tableHidedProps: props?.canAddItems
					? {
							currentMonth: true,
							sectionFirstTitle: "No Auto Alerts in this list.",
							onAddTheFirstOne: handleAddRow,
					  }
					: null,
			}}
		/>
	);
}

AutoAlertsTable.propTypes = {
	autoAlertsSetupId: PropTypes.string,
	locationId: PropTypes.string,
	commissionCycleId: PropTypes.string,
	data: PropTypes.array,
	dateRange: PropTypes.object,
	canAddItems: PropTypes.bool,
	masterPermission: PropTypes.bool,
	selectedItem: PropTypes.shape({
		_id: PropTypes.string,
		effectiveDateFrom: PropTypes.string,
	}),
};

InputEditDateTimeCell.propTypes = {
	id: PropTypes.string,
	field: PropTypes.string,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.instanceOf(Date)]),
	error: PropTypes.bool,
	fromDate: PropTypes.string,
	toDate: PropTypes.string,
};

InputEditActivityTypeCell.propTypes = {
	id: PropTypes.string,
	field: PropTypes.string,
};

InputEditActionTypeCell.propTypes = {
	id: PropTypes.string,
	field: PropTypes.string,
};

InputEditCommissionCell.propTypes = {
	id: PropTypes.string,
	field: PropTypes.string,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	hasFocus: PropTypes.bool,
};

InputEditAssocWindowCell.propTypes = {
	id: PropTypes.string,
	field: PropTypes.string,
	value: PropTypes.string,
	error: PropTypes.bool,
};

DescriptionPopover.propTypes = {
	descriptions: PropTypes.array,
};
